import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, X, RefreshCw, CreditCard, ChevronRight, AlertTriangle, Package, Ticket, Receipt, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import BookingReceipt from '../components/BookingReceipt';
import { getKioskToken, fetchKioskTransactionsData, KIOSK_API_BASE } from '../lib/kioskTokenManager';

// Normalize backend URL (supports both base host and full API base)
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

// Kiosk API endpoints are imported from shared kioskTokenManager

// Pickleball Court Icon Component
const CourtIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <line x1="12" y1="4" x2="12" y2="20" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <circle cx="6" cy="8" r="1" fill="currentColor" />
    <circle cx="18" cy="16" r="1" fill="currentColor" />
  </svg>
);

// Helper function to format currency with trailing zeroes
const formatCurrency = (amount) => {
  return `₱${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to format date as MM/DD/YYYY
const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

// Helper function to convert time to 12-hour format
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  
  let hour;
  // Handle 24-hour format like "14:00"
  if (timeStr.includes(':')) {
    hour = parseInt(timeStr.split(':')[0]);
  } else {
    // Already in AM/PM format
    return timeStr;
  }
  
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:00 ${period}`;
};

// Helper function to format booking time display (handles non-consecutive slots)
const formatBookingTime = (booking) => {
  // If booking has time_slots array with non-consecutive slots
  if (booking.time_slots && booking.time_slots.length > 0) {
    const slots = [...booking.time_slots].sort((a, b) => 
      parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
    );
    
    // Check if slots are consecutive
    let isConsecutive = true;
    for (let i = 1; i < slots.length; i++) {
      const prevHour = parseInt(slots[i-1].split(':')[0]);
      const currHour = parseInt(slots[i].split(':')[0]);
      if (currHour - prevHour !== 1) {
        isConsecutive = false;
        break;
      }
    }
    
    if (!isConsecutive) {
      // Non-consecutive: show as comma-separated times
      return slots.map(slot => formatTime12Hour(slot)).join(', ');
    }
    
    // Consecutive: show as range
    const startTime = slots[0];
    const lastHour = parseInt(slots[slots.length - 1].split(':')[0]);
    const endTime = `${String(lastHour + 1).padStart(2, '0')}:00`;
    return `${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)}`;
  }
  
  // Fallback to legacy start_time/end_time
  return `${formatTime12Hour(booking.start_time)} - ${formatTime12Hour(booking.end_time)}`;
};

// Helper function to format payment method for display
const formatPaymentMethod = (method) => {
  const methods = {
    'credit_debit_card': 'Credit/Debit Card',
    'bank_transfer': 'Bank Transfer',
    'gcash': 'GCash',
    'maya': 'Maya',
    'qrph': 'QR Ph',
    'cash': 'Cash',
    'online_banking': 'Online Banking'
  };
  return methods[method] || method || 'Not specified';
};

// Generate time slots (6AM to 10PM)
const generateTimeSlots = () => {
  const slots = [];
  for (let h = 6; h < 22; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
  }
  return slots;
};

const MyBookingsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  
  // Kiosk transactions state
  const [kioskTransactions, setKioskTransactions] = useState([]);
  const [loadingKiosk, setLoadingKiosk] = useState(false);
  const [selectedKioskTransaction, setSelectedKioskTransaction] = useState(null);
  const [showKioskModal, setShowKioskModal] = useState(false);
  const [kioskModalLoading, setKioskModalLoading] = useState(false);
  
  // Modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [venueInfo, setVenueInfo] = useState(null);
  
  // Receipt modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptBooking, setReceiptBooking] = useState(null);
  
  // Rebook modal state
  const [showRebookModal, setShowRebookModal] = useState(false);
  const [rebookDate, setRebookDate] = useState(new Date());
  const [rebookCourt, setRebookCourt] = useState('');
  const [rebookTimeSlots, setRebookTimeSlots] = useState([]); // Changed to array for multiple slots
  const [rebooking, setRebooking] = useState(false);
  const [venue, setVenue] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [pricingRules, setPricingRules] = useState([]);
  const [slotPrices, setSlotPrices] = useState({});  // Map of slot time -> price info

  // Fetch kiosk transactions for current user (uses shared token manager)
  const fetchKioskTransactions = useCallback(async () => {
    setLoadingKiosk(true);
    try {
      const txArray = await fetchKioskTransactionsData();
      
      // Filter transactions for current user (by email or phone if available)
      const userEmail = user?.email?.toLowerCase();
      const userPhone = user?.phone;
      
      const userTransactions = txArray.filter(tx => {
        // Check if transaction belongs to current user
        const txEmail = tx.customer_email?.toLowerCase() || tx.email?.toLowerCase();
        const txPhone = tx.customer_phone || tx.phone;
        
        if (userEmail && txEmail && txEmail === userEmail) return true;
        if (userPhone && txPhone && txPhone === userPhone) return true;
        
        // Also check booking_infos for customer details
        const bookingInfos = tx.booking_infos || [];
        for (const info of bookingInfos) {
          const infoEmail = info.customer_email?.toLowerCase() || info.email?.toLowerCase();
          const infoPhone = info.customer_phone || info.phone;
          if (userEmail && infoEmail && infoEmail === userEmail) return true;
          if (userPhone && infoPhone && infoPhone === userPhone) return true;
        }
        
        return false;
      });
      
      console.log(`Fetched ${userTransactions.length} kiosk transactions for user`);
      setKioskTransactions(userTransactions);
    } catch (error) {
      console.error('Failed to fetch kiosk transactions:', error);
    } finally {
      setLoadingKiosk(false);
    }
  }, [user]);

  // Handle kiosk transaction click - fetch full details
  const handleKioskTransactionClick = async (transactionCode) => {
    setShowKioskModal(true);
    setKioskModalLoading(true);
    setSelectedKioskTransaction(null);

    try {
      const kioskToken = await getKioskToken();
      if (!kioskToken) {
        toast.error('Failed to authenticate with kiosk API');
        setShowKioskModal(false);
        return;
      }

      const url = `${KIOSK_API_BASE}/transactions/${transactionCode}`;
      const res = await fetch(url, {
        headers: { 'X-KIOSK-TOKEN': kioskToken },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch transaction details: ${res.status}`);
      }

      const responseData = await res.json();
      const txData = responseData?.data || responseData;
      setSelectedKioskTransaction(txData);
    } catch (error) {
      console.error('Failed to fetch transaction details:', error);
      toast.error(`Failed to load transaction: ${error.message}`);
      setShowKioskModal(false);
    } finally {
      setKioskModalLoading(false);
    }
  };

  // Close kiosk modal
  const closeKioskModal = () => {
    setShowKioskModal(false);
    setSelectedKioskTransaction(null);
  };

  // Convert kiosk transaction to booking-like format for display
  const convertKioskToBooking = (tx) => {
    const bookingInfos = tx.booking_infos || [];
    const firstInfo = bookingInfos[0] || {};
    
    // Extract time slots from booking info
    let timeSlots = [];
    let startTime = '';
    let endTime = '';
    
    if (firstInfo.time_slots && firstInfo.time_slots.length > 0) {
      timeSlots = firstInfo.time_slots;
      const sortedSlots = [...timeSlots].sort();
      startTime = sortedSlots[0];
      const lastHour = parseInt(sortedSlots[sortedSlots.length - 1]?.split(':')[0] || sortedSlots[sortedSlots.length - 1]?.replace(/[AP]M/i, '')) || 0;
      endTime = `${String(lastHour + 1).padStart(2, '0')}:00`;
    } else if (firstInfo.booking_time_start) {
      startTime = firstInfo.booking_time_start;
      endTime = firstInfo.booking_time_end || '';
    }
    
    // Determine court_id
    const courtNo = firstInfo.court_no || firstInfo.court || 1;
    const courtId = `court_${courtNo}`;
    
    // Calculate duration
    const duration = timeSlots.length || 1;
    
    return {
      booking_id: `kiosk_${tx.transaction_code}`,
      transaction_code: tx.transaction_code,
      isKiosk: true,
      user_id: tx.customer_email || '',
      venue_id: 'venue_camsur',
      venue_name: 'CamSur Watersports Complex',
      court_id: courtId,
      date: firstInfo.booking_date || tx.created_at?.split('T')[0] || '',
      time_slots: timeSlots,
      start_time: startTime,
      end_time: endTime,
      duration_hours: duration,
      status: tx.status === 'paid' ? 'confirmed' : (tx.status === 'cancelled' ? 'cancelled' : 'pending'),
      payment_status: tx.status || 'pending',
      total_amount: tx.total_amount || 0,
      equipment_rentals: [],
      add_ons: [],
      created_at: tx.created_at,
      customer_name: firstInfo.customer_name || tx.customer_name || '',
      booking_infos: bookingInfos
    };
  };

  const fetchPricingRules = useCallback(async () => {
    try {
      const response = await fetch(`${API}/pricing-rules`);
      if (!response.ok) {
        throw new Error('Failed to fetch pricing rules');
      }
      const data = await response.json();
      setPricingRules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pricing rules:', error);
      setPricingRules([]);
    }
  }, []);

  const fetchVenue = useCallback(async () => {
    try {
      const response = await fetch(`${API}/venues/venue_camsur`);
      if (!response.ok) {
        throw new Error('Failed to fetch venue');
      }
      const data = await response.json();
      setVenue(data);
    } catch (error) {
      console.error('Error fetching venue:', error);
    }
  }, []);

  const fetchVenueInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API}/venue-info`);
      if (!response.ok) {
        throw new Error('Failed to fetch venue info');
      }
      const data = await response.json();
      setVenueInfo(data);
    } catch (error) {
      console.error('Error fetching venue info:', error);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to load bookings' }));
        throw new Error(errorData.detail || 'Failed to load bookings');
      }
      
      const data = await response.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError(error.message || 'Failed to load bookings');
      toast.error(error.message || 'Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Combined fetch for all data
  const fetchAllData = useCallback(async () => {
    await Promise.all([
      fetchBookings(),
      fetchKioskTransactions(),
      fetchVenueInfo(),
      fetchVenue(),
      fetchPricingRules()
    ]);
  }, [fetchBookings, fetchKioskTransactions, fetchVenueInfo, fetchVenue, fetchPricingRules]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Combine regular bookings with kiosk transactions
  const allBookings = useMemo(() => {
    const kioskBookings = kioskTransactions.map(convertKioskToBooking);
    // Merge and sort by date (most recent first)
    const combined = [...bookings, ...kioskBookings];
    return combined.sort((a, b) => {
      const dateA = new Date(a.date + 'T' + (a.start_time || '00:00'));
      const dateB = new Date(b.date + 'T' + (b.start_time || '00:00'));
      return dateB - dateA;
    });
  }, [bookings, kioskTransactions]);

  // Helper function to determine if an hour is peak based on pricing rules
  const getPriceInfoForSlot = (hour, date) => {
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const dayType = isWeekend ? 'weekend' : 'weekday';
    const defaultPrice = venue?.price_per_hour || 500;
    
    for (const rule of pricingRules) {
      if (!rule.is_active) continue;
      if (rule.day_type && rule.day_type !== dayType) continue;
      
      if (rule.start_time && rule.end_time) {
        const ruleStart = parseInt(rule.start_time.split(':')[0]);
        let ruleEnd = parseInt(rule.end_time.split(':')[0]);
        if (rule.end_time.includes(':59')) ruleEnd += 1;
        
        if (hour < ruleStart || hour >= ruleEnd) continue;
      }
      
      const price = rule.price_per_hour || defaultPrice;
      // Peak is considered if price is higher than default
      return { price, isPeak: price > defaultPrice };
    }
    
    return { price: defaultPrice, isPeak: false };
  };

  // Calculate peak/regular breakdown for a set of time slots
  const calculatePeakRegularBreakdown = (slots, date) => {
    let peakHours = 0;
    let regularHours = 0;
    
    slots.forEach(slot => {
      const hour = parseInt(slot.split(':')[0]);
      const priceInfo = getPriceInfoForSlot(hour, date);
      if (priceInfo.isPeak) {
        peakHours++;
      } else {
        regularHours++;
      }
    });
    
    return { peakHours, regularHours };
  };

  // Get original booking's peak/regular breakdown
  const getOriginalBookingBreakdown = () => {
    if (!selectedBooking) return { peakHours: 0, regularHours: 0 };
    
    // If booking has stored peak/regular hours, use them
    if (selectedBooking.peak_hours !== undefined && selectedBooking.regular_hours !== undefined) {
      return {
        peakHours: selectedBooking.peak_hours,
        regularHours: selectedBooking.regular_hours
      };
    }
    
    // Otherwise calculate from the booking's time slots
    const startHour = parseTimeToHour(selectedBooking.start_time);
    const duration = selectedBooking.duration_hours || 1;
    const bookingDate = new Date(selectedBooking.date + 'T00:00:00');
    
    const slots = [];
    for (let i = 0; i < duration; i++) {
      slots.push(`${String(startHour + i).padStart(2, '0')}:00`);
    }
    
    return calculatePeakRegularBreakdown(slots, bookingDate);
  };

  // Get selected slots' peak/regular breakdown
  const getSelectedSlotsBreakdown = () => {
    return calculatePeakRegularBreakdown(rebookTimeSlots, rebookDate);
  };

  // Check if selected slots match original booking's peak/regular breakdown
  const doesBreakdownMatch = () => {
    const original = getOriginalBookingBreakdown();
    const selected = getSelectedSlotsBreakdown();
    return original.peakHours === selected.peakHours && original.regularHours === selected.regularHours;
  };

  const fetchAvailability = async (date, courtId) => {
    if (!date || !courtId) return;
    
    setLoadingAvailability(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await fetch(`${API}/venues/venue_camsur/availability?date=${dateStr}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }
      
      const data = await response.json();
      // Extract booked slots for the selected court
      const bookedSlots = (data?.slots || [])
        .filter(slot => slot.court_id === courtId && !slot.available)
        .map(slot => slot.start_time);
      setAvailability(bookedSlots);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailability([]);
      toast.error('Failed to load time slot availability');
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedBooking) {
      toast.error('No booking selected');
      return;
    }
    
    setCancelling(true);
    try {
      const response = await fetch(`${API}/bookings/${selectedBooking.booking_id}/cancel`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: cancelReason || 'Cancelled by user' })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to cancel booking' }));
        throw new Error(errorData.detail || 'Failed to cancel booking');
      }
      
      toast.success('Booking cancelled successfully');
      setShowCancelModal(false);
      setShowDetailsModal(false);
      setCancelReason('');
      await fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.message || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const openRebookModal = () => {
    if (!selectedBooking) {
      toast.error('No booking selected');
      return;
    }
    
    // Reset all rebook state
    setRebookDate(new Date(selectedBooking.date + 'T00:00:00'));
    setRebookCourt(selectedBooking.court_id);
    setRebookTimeSlots([]);
    setAvailability([]);
    
    // Open modal and close details
    setShowRebookModal(true);
    setShowDetailsModal(false);
    
    // Fetch availability for the current date and court
    fetchAvailability(new Date(selectedBooking.date + 'T00:00:00'), selectedBooking.court_id);
  };

  const handleRebookDateChange = (date) => {
    setRebookDate(date);
    setRebookTimeSlots([]); // Reset time slots when date changes
    if (rebookCourt) {
      fetchAvailability(date, rebookCourt);
    }
  };

  const handleRebookCourtChange = (courtId) => {
    setRebookCourt(courtId);
    setRebookTimeSlots([]); // Reset time slots when court changes
    fetchAvailability(rebookDate, courtId);
  };

  const isSlotAvailable = (slot) => {
    // Check if slot is in the past for today
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    const selectedDateStr = format(rebookDate, 'yyyy-MM-dd');
    
    if (selectedDateStr === todayStr) {
      const slotHour = parseInt(slot.split(':')[0]);
      if (slotHour <= now.getHours()) return false;
    }
    
    // Check if slot is booked (from availability data)
    return !availability.includes(slot);
  };

  // Handle slot selection for rescheduling
  const handleRebookSlotClick = (slot) => {
    const requiredDuration = selectedBooking?.duration_hours || 1;
    
    setRebookTimeSlots(prev => {
      // If already selected, remove it
      if (prev.includes(slot)) {
        return prev.filter(s => s !== slot);
      }
      
      // Add the slot
      const newSlots = [...prev, slot].sort((a, b) => 
        parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
      );
      
      // If we have more than required slots, keep only the most recent selection context
      if (newSlots.length > requiredDuration) {
        // Keep slots that form a consecutive block with the new slot
        const slotHour = parseInt(slot.split(':')[0]);
        const consecutiveSlots = [slot];
        
        // Look for consecutive slots before
        for (let h = slotHour - 1; h >= 6 && consecutiveSlots.length < requiredDuration; h--) {
          const checkSlot = `${String(h).padStart(2, '0')}:00`;
          if (newSlots.includes(checkSlot) && isSlotAvailable(checkSlot)) {
            consecutiveSlots.unshift(checkSlot);
          } else {
            break;
          }
        }
        
        // Look for consecutive slots after
        for (let h = slotHour + 1; h < 22 && consecutiveSlots.length < requiredDuration; h++) {
          const checkSlot = `${String(h).padStart(2, '0')}:00`;
          if (newSlots.includes(checkSlot) && isSlotAvailable(checkSlot)) {
            consecutiveSlots.push(checkSlot);
          } else {
            break;
          }
        }
        
        return consecutiveSlots.slice(0, requiredDuration);
      }
      
      return newSlots;
    });
  };

  // Get formatted display for selected time slots (handles non-consecutive)
  const getRebookTimeSlotsDisplay = () => {
    if (rebookTimeSlots.length === 0) return '';
    const sorted = [...rebookTimeSlots].sort((a, b) => 
      parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
    );
    
    // Group consecutive slots into ranges
    const ranges = [];
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];
    
    for (let i = 1; i < sorted.length; i++) {
      const prevHour = parseInt(rangeEnd.split(':')[0]);
      const currHour = parseInt(sorted[i].split(':')[0]);
      
      if (currHour - prevHour === 1) {
        rangeEnd = sorted[i];
      } else {
        const endHour = parseInt(rangeEnd.split(':')[0]) + 1;
        ranges.push(`${formatTime12Hour(rangeStart)} - ${formatTime12Hour(`${endHour.toString().padStart(2, '0')}:00`)}`);
        rangeStart = sorted[i];
        rangeEnd = sorted[i];
      }
    }
    // Add last range
    const endHour = parseInt(rangeEnd.split(':')[0]) + 1;
    ranges.push(`${formatTime12Hour(rangeStart)} - ${formatTime12Hour(`${endHour.toString().padStart(2, '0')}:00`)}`);
    
    return ranges.join(', ');
  };

  const handleRebook = async () => {
    const requiredDuration = selectedBooking?.duration_hours || 1;
    
    if (!selectedBooking) {
      toast.error('No booking selected');
      return;
    }
    
    if (!rebookCourt) {
      toast.error('Please select a court');
      return;
    }
    
    if (rebookTimeSlots.length !== requiredDuration) {
      toast.error(`Please select exactly ${requiredDuration} hour${requiredDuration > 1 ? 's' : ''} to match your original booking`);
      return;
    }

    // Check peak/regular hours match
    if (!doesBreakdownMatch()) {
      const original = getOriginalBookingBreakdown();
      toast.error(`New time must have ${original.peakHours} peak and ${original.regularHours} regular hour(s) to keep the same price`);
      return;
    }

    const sortedSlots = [...rebookTimeSlots].sort((a, b) => 
      parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
    );
    const dateStr = format(rebookDate, 'yyyy-MM-dd');

    setRebooking(true);
    try {
      // First cancel the old booking
      const cancelResponse = await fetch(`${API}/bookings/${selectedBooking.booking_id}/cancel`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Rescheduled to new time slot' })
      });
      
      if (!cancelResponse.ok) {
        throw new Error('Failed to cancel original booking');
      }

      // Create new booking with time_slots array (supports non-consecutive)
      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          venue_id: selectedBooking.venue_id,
          court_id: rebookCourt,
          date: dateStr,
          time_slots: sortedSlots,
          duration_hours: requiredDuration,
          equipment_rentals: selectedBooking.equipment_rentals || selectedBooking.add_ons || [],
          payment_status: 'paid',
          payment_method: selectedBooking.payment_method || 'credit_debit_card'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create new booking' }));
        throw new Error(errorData.detail || 'Failed to reschedule');
      }
      
      toast.success('Booking rescheduled successfully!');
      setShowRebookModal(false);
      setRebookTimeSlots([]);
      await fetchBookings();
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      toast.error(error.message || 'Failed to reschedule booking');
    } finally {
      setRebooking(false);
    }
  };

  // Transform booking to payment format for receipt
  const transformBookingToPayment = (booking) => {
    return {
      id: booking.booking_id,
      transaction_code: booking.payment_transaction_id || booking.booking_id?.slice(-8).toUpperCase(),
      name: user?.name || 'Customer',
      contact_number: user?.phone || '',
      email: user?.email || '',
      payment_type: booking.payment_method || 'online',
      payment_status: booking.payment_status || 'paid',
      total_amount: Number(booking.total_amount || 0).toFixed(2),
      created_at: booking.created_at || new Date().toISOString(),
      venue_name: venueInfo?.name || booking.venue_name || 'CamSur Pickleball Club',
      booking_infos: [{
        id: 1,
        court_id: booking.court_id,
        court_no: booking.court_id?.replace(/\D/g, '') || '1',
        court_name: booking.court_name || `Court ${booking.court_id}`,
        player_count: booking.num_players || 2,
        booking_date: booking.date,
        booking_time_start: booking.start_time || (booking.time_slots?.[0] + ':00') || '00:00:00',
        booking_time_end: booking.end_time || '00:00:00',
        time_slots: booking.time_slots || [],
        duration_hours: booking.duration_hours || 1
      }],
      equipment_rentals: booking.equipment_rentals || [],
      add_ons: booking.add_ons || []
    };
  };

  // Handle view receipt
  const handleViewReceipt = (booking, e) => {
    if (e) {
      e.stopPropagation();
    }
    setReceiptBooking(transformBookingToPayment(booking));
    setShowReceiptModal(true);
  };

  // Handle quick cancel from card
  const handleQuickCancel = (booking, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedBooking(booking);
    setShowCancelModal(true);
  };

  const openBookingDetails = (booking) => {
    if (!booking) {
      toast.error('Invalid booking');
      return;
    }
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed': return <Badge className="bg-green-100 text-green-800">Confirmed</Badge>;
      case 'cancelled': return <Badge variant="destructive">Cancelled</Badge>;
      case 'completed': return <Badge variant="secondary">Completed</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <Badge className="bg-emerald-100 text-emerald-800">Paid</Badge>;
      case 'unpaid': return <Badge className="bg-amber-100 text-amber-800">Unpaid</Badge>;
      default: return <Badge variant="outline">{status || 'Pending'}</Badge>;
    }
  };

  // Helper to parse time string to hour (handles both "14:00" and "2PM" formats)
  const parseTimeToHour = (timeStr) => {
    if (!timeStr) return 0;
    
    if (timeStr.includes(':')) {
      return parseInt(timeStr.split(':')[0]);
    }
    
    const match = timeStr.match(/(\d+)(AM|PM)/i);
    if (match) {
      let hour = parseInt(match[1]);
      if (match[2].toUpperCase() === 'PM' && hour !== 12) hour += 12;
      if (match[2].toUpperCase() === 'AM' && hour === 12) hour = 0;
      return hour;
    }
    
    return 0;
  };

  // Get booking status based on current time
  const getBookingTimeStatus = useCallback((booking) => {
    if (!booking || !booking.date) return 'past';
    if (booking.status === 'cancelled') return 'cancelled';

    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const currentHour = now.getHours();
    const bookingDate = booking.date;

    // Determine start and end times - handle both time_slots array and legacy start_time/end_time
    let startHour, endHour;

    if (booking.time_slots && booking.time_slots.length > 0) {
      // Use time_slots array - sort to get earliest and latest
      const sortedSlots = [...booking.time_slots].sort((a, b) =>
        parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
      );
      startHour = parseTimeToHour(sortedSlots[0]);
      // End hour is the last slot hour + 1 (since slots are 1-hour each)
      const lastSlotHour = parseInt(sortedSlots[sortedSlots.length - 1].split(':')[0]);
      endHour = lastSlotHour + 1;
    } else {
      // Use legacy start_time/end_time
      startHour = parseTimeToHour(booking.start_time);
      endHour = parseTimeToHour(booking.end_time);
    }

    // Future date = upcoming
    if (bookingDate > todayStr) return 'upcoming';

    // Past date = past
    if (bookingDate < todayStr) return 'past';

    // Today - check time
    if (bookingDate === todayStr) {
      // Current time is before booking start = upcoming
      if (currentHour < startHour) return 'upcoming';

      // Current time is within booking window = ongoing
      if (currentHour >= startHour && currentHour < endHour) return 'ongoing';

      // Current time is after booking end = past
      if (currentHour >= endHour) return 'past';
    }

    return 'past';
  }, []);

  // Filter bookings by status with useMemo for optimization (using allBookings which includes kiosk)
  const ongoingBookings = useMemo(() => 
    allBookings.filter(b => b.status === 'confirmed' && getBookingTimeStatus(b) === 'ongoing'),
    [allBookings, getBookingTimeStatus]
  );
  
  const upcomingBookings = useMemo(() => 
    allBookings.filter(b => (b.status === 'confirmed') && getBookingTimeStatus(b) === 'upcoming'),
    [allBookings, getBookingTimeStatus]
  );
  
  const pastBookings = useMemo(() => 
    allBookings.filter(b => (b.status === 'confirmed') && getBookingTimeStatus(b) === 'past'),
    [allBookings, getBookingTimeStatus]
  );
  
  const cancelledBookings = useMemo(() => 
    allBookings.filter(b => b.status === 'cancelled'),
    [allBookings]
  );
  
  // Filter pending bookings (status === 'pending')
  const pendingBookings = useMemo(() => 
    allBookings.filter(b => b.status === 'pending' && !b.isKiosk),
    [allBookings]
  );
  
  // Filter kiosk-only transactions (pending payment)
  const pendingKioskBookings = useMemo(() => 
    allBookings.filter(b => b.isKiosk && b.payment_status === 'pending'),
    [allBookings]
  );
  
  // Helper to check if booking is upcoming only (for rebook button - not ongoing, past, or cancelled)
  const isUpcomingOnly = (booking) => {
    return (booking?.status === 'confirmed' || booking?.status === 'pending') && getBookingTimeStatus(booking) === 'upcoming';
  };

  // Helper to format court name
  const formatCourtName = (courtId) => {
    if (!courtId) return 'Court';
    return courtId.replace('court_', 'Court ');
  };

  // Get the venue address (from venueInfo or fallback to booking's venue_address)
  const getVenueAddress = () => {
    return venueInfo?.address || '';
  };

  // Open booking details - handles both regular and kiosk bookings
  const handleBookingClick = (booking) => {
    if (booking.isKiosk) {
      handleKioskTransactionClick(booking.transaction_code);
    } else {
      openBookingDetails(booking);
    }
  };

  const BookingCard = ({ booking }) => (
    <Card 
      className={`card-hover cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${booking.isKiosk ? 'border-l-4 border-l-purple-500' : ''}`}
      data-testid={`booking-${booking.booking_id}`}
      onClick={() => handleBookingClick(booking)}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-heading font-semibold text-lg">{venueInfo?.name || booking.venue_name}</h3>
              {getStatusBadge(booking.status)}
              {booking.isKiosk && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                  <Ticket className="h-3 w-3 mr-1" />
                  Kiosk
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground flex items-center mb-1">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">{getVenueAddress() || booking.venue_address}</span>
            </p>
            <p className="text-sm text-muted-foreground flex items-center mb-1">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(booking.date)}
            </p>
            <p className="text-sm text-muted-foreground flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {formatBookingTime(booking)} ({booking.duration_hours}hr{booking.duration_hours > 1 ? 's' : ''})
            </p>
            <p className="text-sm font-medium text-primary mt-1 flex items-center">
              <CourtIcon className="h-4 w-4 mr-1" />
              {formatCourtName(booking.court_id)}
            </p>
            
            {(booking.equipment_rentals?.length > 0 || booking.add_ons?.length > 0) && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground flex items-center mb-1">
                  <Package className="h-3 w-3 mr-1" />
                  Add-ons:
                </p>
                <div className="flex flex-wrap gap-1">
                  {(booking.equipment_rentals || booking.add_ons || []).map((item, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {item.name} x{item.quantity || 1}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            {!booking.isKiosk && (
              <div className="flex gap-2 mt-3">
                {/* Cancel button for pending bookings */}
                {booking.status === 'pending' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    onClick={(e) => handleQuickCancel(booking, e)}
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Cancel
                  </Button>
                )}
                
                {/* View Receipt button for confirmed bookings */}
                {booking.status === 'confirmed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                    onClick={(e) => handleViewReceipt(booking, e)}
                  >
                    <Receipt className="h-3 w-3 mr-1" />
                    View Receipt
                  </Button>
                )}
              </div>
            )}
          </div>
          
          <div className="text-right flex flex-col items-end">
            <p className="text-xl font-heading font-bold text-primary">{formatCurrency(booking.total_amount)}</p>
            <ChevronRight className="h-5 w-5 text-muted-foreground mt-2" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-bookings-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">My Bookings</h1>
            <p className="text-muted-foreground">Manage your court reservations</p>
            {error && (
              <p className="text-sm text-destructive mt-1 flex items-center gap-2">
                <AlertTriangle className="h-3 w-3" />
                {error}
                <Button 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 text-xs underline"
                  onClick={fetchAllData}
                >
                  Retry
                </Button>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchAllData}
              disabled={loading || loadingKiosk}
              title="Refresh bookings"
            >
              <RefreshCw className={`h-4 w-4 ${(loading || loadingKiosk) ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={() => navigate('/book/venue_camsur')} className="btn-primary">
              Book New Court
            </Button>
          </div>
        </div>

        <Tabs defaultValue={ongoingBookings.length > 0 ? "ongoing" : (pendingKioskBookings.length > 0 ? "pending" : "upcoming")}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="ongoing" data-testid="tab-ongoing" className="relative">
              On-Going ({ongoingBookings.length})
              {ongoingBookings.length > 0 && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
              )}
            </TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              Upcoming ({upcomingBookings.length})
            </TabsTrigger>
            {pendingKioskBookings.length > 0 && (
              <TabsTrigger value="pending" data-testid="tab-pending" className="relative">
                Pending ({pendingKioskBookings.length})
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-amber-500 rounded-full animate-pulse"></span>
              </TabsTrigger>
            )}
            <TabsTrigger value="past" data-testid="tab-past">
              Past ({pastBookings.length})
            </TabsTrigger>
            {pendingBookings.length > 0 && (
              <TabsTrigger value="pending-status" data-testid="tab-pending-status" className="relative">
                Pending ({pendingBookings.length})
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-amber-500 rounded-full animate-pulse"></span>
              </TabsTrigger>
            )}
            <TabsTrigger value="cancelled" data-testid="tab-cancelled">
              Cancelled ({cancelledBookings.length})
            </TabsTrigger>
          </TabsList>

          {/* Pending Kiosk Bookings Tab */}
          {pendingKioskBookings.length > 0 && (
            <TabsContent value="pending">
              {loadingKiosk ? (
                <div className="space-y-4">
                  {[1, 2].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-xl"></div>)}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800">
                      <AlertTriangle className="h-4 w-4 inline mr-2" />
                      These bookings were made at the kiosk and are awaiting payment confirmation.
                    </p>
                  </div>
                  {pendingKioskBookings.map((booking) => (
                    <Card 
                      key={booking.booking_id}
                      className="card-hover cursor-pointer transition-all hover:shadow-md border-l-4 border-l-amber-500 bg-amber-50/50" 
                      data-testid={`booking-${booking.booking_id}`}
                      onClick={() => handleBookingClick(booking)}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-heading font-semibold text-lg">{venueInfo?.name || booking.venue_name}</h3>
                              <Badge className="bg-amber-100 text-amber-800">Pending Payment</Badge>
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <Ticket className="h-3 w-3 mr-1" />
                                Kiosk
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground flex items-center mb-1">
                              <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                              <span className="truncate">{getVenueAddress() || booking.venue_address}</span>
                            </p>
                            <p className="text-sm text-muted-foreground flex items-center mb-1">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(booking.date)}
                            </p>
                            <p className="text-sm text-muted-foreground flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {formatBookingTime(booking)} ({booking.duration_hours}hr{booking.duration_hours > 1 ? 's' : ''})
                            </p>
                            <p className="text-sm font-medium text-primary mt-1 flex items-center">
                              <CourtIcon className="h-4 w-4 mr-1" />
                              {formatCourtName(booking.court_id)}
                            </p>
                          </div>
                          
                          <div className="text-right flex flex-col items-end">
                            <p className="text-xl font-heading font-bold text-amber-600">{formatCurrency(booking.total_amount)}</p>
                            <ChevronRight className="h-5 w-5 text-muted-foreground mt-2" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          <TabsContent value="ongoing">
            {loading ? (
              <div className="space-y-4">
                {[1].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-xl"></div>)}
              </div>
            ) : ongoingBookings.length > 0 ? (
              <div className="space-y-4">
                {ongoingBookings.map((booking) => (
                  <Card 
                    key={booking.booking_id}
                    className={`card-hover cursor-pointer transition-all hover:shadow-md border-2 border-green-500 bg-green-50 ${booking.isKiosk ? 'border-l-4 border-l-purple-500' : ''}`}
                    data-testid={`booking-${booking.booking_id}`}
                    onClick={() => handleBookingClick(booking)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-heading font-semibold text-lg">{venueInfo?.name || booking.venue_name}</h3>
                            <Badge className="bg-green-500 text-white animate-pulse">
                              <span className="mr-1">●</span> Live Now
                            </Badge>
                            {booking.isKiosk && (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <Ticket className="h-3 w-3 mr-1" />
                                Kiosk
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center mb-1">
                            <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span className="truncate">{getVenueAddress() || booking.venue_address}</span>
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center mb-1">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(booking.date)}
                          </p>
                          <p className="text-sm font-medium text-green-700 flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatBookingTime(booking)} ({booking.duration_hours}hr{booking.duration_hours > 1 ? 's' : ''})
                          </p>
                          <p className="text-sm font-medium text-green-700 mt-1 flex items-center">
                            <CourtIcon className="h-4 w-4 mr-1" />
                            {formatCourtName(booking.court_id)}
                          </p>
                          {(booking.equipment_rentals?.length > 0 || booking.add_ons?.length > 0) && (
                            <div className="mt-3">
                              <p className="text-xs text-green-600 flex items-center mb-1">
                                <Package className="h-3 w-3 mr-1" />
                                Add-ons:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {(booking.equipment_rentals || booking.add_ons || []).map((item, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs border-green-400">
                                    {item.name} x{item.quantity || 1}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right flex flex-col items-end">
                          <p className="text-xl font-heading font-bold text-primary">{formatCurrency(booking.total_amount)}</p>
                          <ChevronRight className="h-5 w-5 text-muted-foreground mt-2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">No on-going bookings</h3>
                <p className="text-muted-foreground mb-4">You don&apos;t have any bookings happening right now</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-xl"></div>)}
              </div>
            ) : upcomingBookings.length > 0 ? (
              <div className="space-y-4">
                {upcomingBookings.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">No upcoming bookings</h3>
                <p className="text-muted-foreground mb-4">Time to book your next game!</p>
                <Button onClick={() => navigate('/book/venue_camsur')} className="btn-primary">
                  Book a Court
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastBookings.length > 0 ? (
              <div className="space-y-4">
                {pastBookings.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No past bookings</p>
              </div>
            )}
          </TabsContent>

          {pendingBookings.length > 0 && (
            <TabsContent value="pending-status">
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4 inline mr-2" />
                    These bookings are pending confirmation. You can cancel them if needed.
                  </p>
                </div>
                {pendingBookings.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            </TabsContent>
          )}

          <TabsContent value="cancelled">
            {cancelledBookings.length > 0 ? (
              <div className="space-y-4">
                {cancelledBookings.map((booking) => (
                  <Card 
                    key={booking.booking_id}
                    className={`card-hover cursor-pointer transition-all hover:shadow-md border-l-4 border-l-red-500 opacity-75 ${booking.isKiosk ? 'border-r-4 border-r-purple-500' : ''}`}
                    data-testid={`booking-${booking.booking_id}`}
                    onClick={() => handleBookingClick(booking)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-heading font-semibold text-lg line-through text-muted-foreground">{venueInfo?.name || booking.venue_name}</h3>
                            <Badge variant="destructive">Cancelled</Badge>
                            {booking.isKiosk && (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <Ticket className="h-3 w-3 mr-1" />
                                Kiosk
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center mb-1">
                            <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span className="truncate">{getVenueAddress() || booking.venue_address}</span>
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center mb-1">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(booking.date)}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatBookingTime(booking)} ({booking.duration_hours}hr{booking.duration_hours > 1 ? 's' : ''})
                          </p>
                          <p className="text-sm text-muted-foreground mt-1 flex items-center">
                            <CourtIcon className="h-4 w-4 mr-1" />
                            {formatCourtName(booking.court_id)}
                          </p>
                          {(booking.equipment_rentals?.length > 0 || booking.add_ons?.length > 0) && (
                            <div className="mt-3">
                              <p className="text-xs text-muted-foreground flex items-center mb-1">
                                <Package className="h-3 w-3 mr-1" />
                                Add-ons:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {(booking.equipment_rentals || booking.add_ons || []).map((item, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {item.name} x{item.quantity || 1}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right flex flex-col items-end">
                          <p className="text-xl font-heading font-bold text-muted-foreground line-through">{formatCurrency(booking.total_amount)}</p>
                          <ChevronRight className="h-5 w-5 text-muted-foreground mt-2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <X className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No cancelled bookings</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={(open) => {
        setShowDetailsModal(open);
        if (!open) {
          // Small delay before clearing to allow close animation
          setTimeout(() => setSelectedBooking(null), 200);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Booking Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                {getStatusBadge(selectedBooking.status)}
              </div>

              <Separator />

              {/* Booking Information */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Booking Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Venue</span>
                    <p className="font-medium">{selectedBooking.venue_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Court</span>
                    <p className="font-medium">{selectedBooking.court_id?.replace('court_', 'Court ')}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date</span>
                    <p className="font-medium">{formatDate(selectedBooking.date)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Time</span>
                    <p className="font-medium">
                      {formatBookingTime(selectedBooking)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duration</span>
                    <p className="font-medium">{selectedBooking.duration_hours} hour{selectedBooking.duration_hours > 1 ? 's' : ''}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Booking ID</span>
                    <p className="font-medium text-xs">{selectedBooking.booking_id}</p>
                  </div>
                </div>
              </div>

              {/* Add-ons */}
              {(selectedBooking.equipment_rentals?.length > 0 || selectedBooking.add_ons?.length > 0) && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-semibold mb-3">Add-ons</h4>
                    <div className="space-y-2">
                      {(selectedBooking.equipment_rentals || selectedBooking.add_ons || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{item.name} {item.quantity > 1 ? `x${item.quantity}` : ''}</span>
                          <span className="font-medium">{formatCurrency(item.price * (item.quantity || 1))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Payment Information */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Payment Information
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment Status</span>
                    {getPaymentStatusBadge(selectedBooking.payment_status)}
                  </div>
                  {selectedBooking.payment_method && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payment Method</span>
                      <span className="font-medium">{formatPaymentMethod(selectedBooking.payment_method)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">Total Amount</span>
                    <span className="font-bold text-primary text-lg">{formatCurrency(selectedBooking.total_amount)}</span>
                  </div>
                </div>
              </div>

              {/* Booked On */}
              <div className="text-xs text-muted-foreground text-center">
                Booked on {new Date(selectedBooking.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-2">
            {selectedBooking?.status === 'confirmed' && isUpcomingOnly(selectedBooking) && (
              <>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={openRebookModal}
                  disabled={cancelling}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reschedule
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1"
                  onClick={() => setShowCancelModal(true)}
                  disabled={cancelling}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel Booking
                </Button>
              </>
            )}
            {selectedBooking?.status === 'confirmed' && getBookingTimeStatus(selectedBooking) === 'ongoing' && (
              <Button 
                variant="destructive" 
                className="w-full"
                onClick={() => setShowCancelModal(true)}
                disabled={cancelling}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel Booking
              </Button>
            )}
            {selectedBooking?.status === 'cancelled' && (
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </Button>
            )}
            {selectedBooking?.status === 'confirmed' && getBookingTimeStatus(selectedBooking) === 'past' && (
              <Button 
                className="w-full"
                onClick={() => navigate(`/book/${selectedBooking.venue_id}?court=${selectedBooking.court_id}`)}
                disabled={loading}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Book Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog open={showCancelModal} onOpenChange={(open) => {
        if (!cancelling) {
          setShowCancelModal(open);
          if (!open) setCancelReason('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Cancel Booking?
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                You are about to cancel your booking for <strong>{selectedBooking.venue_name}</strong> on{' '}
                <strong>{formatDate(selectedBooking.date)}</strong> at{' '}
                <strong>{formatTime12Hour(selectedBooking.start_time)}</strong>.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="cancel-reason">Reason for cancellation (optional)</Label>
                <Textarea
                  id="cancel-reason"
                  placeholder="Please let us know why you're cancelling..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This action cannot be undone. Please make sure you want to cancel this booking.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCancelModal(false);
                setCancelReason('');
              }}
              disabled={cancelling}
            >
              Keep Booking
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rebook Modal */}
      <Dialog open={showRebookModal} onOpenChange={(open) => {
        if (!rebooking) {
          setShowRebookModal(open);
          if (!open) {
            setRebookTimeSlots([]);
            setAvailability([]);
          }
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Reschedule Booking
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            {/* Original Booking Info with Peak/Regular breakdown */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Original Booking:</strong> {selectedBooking?.duration_hours || 1} hour{(selectedBooking?.duration_hours || 1) > 1 ? 's' : ''}
              </p>
              {(() => {
                const original = getOriginalBookingBreakdown();
                return (
                  <div className="flex gap-3 text-sm">
                    {original.peakHours > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-orange-100 text-orange-700">
                        <span className="w-2 h-2 rounded-full bg-orange-500 mr-1.5"></span>
                        {original.peakHours} Peak hr{original.peakHours > 1 ? 's' : ''}
                      </span>
                    )}
                    {original.regularHours > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700">
                        <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
                        {original.regularHours} Regular hr{original.regularHours > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                );
              })()}
              <p className="text-xs text-blue-600 mt-2">
                Select the same combination to keep your booking price unchanged.
              </p>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label>Select New Date</Label>
              <CalendarComponent
                mode="single"
                selected={rebookDate}
                onSelect={(date) => date && handleRebookDateChange(date)}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-md border w-full flex justify-center"
              />
            </div>

            {/* Court Selection */}
            <div className="space-y-2">
              <Label>Select Court</Label>
              <Select value={rebookCourt} onValueChange={handleRebookCourtChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a court" />
                </SelectTrigger>
                <SelectContent>
                  {venue?.courts?.map((court) => (
                    <SelectItem key={court.court_id} value={court.court_id}>
                      {court.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Slot Legend */}
            <div className="flex flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded border-2 border-orange-400 bg-orange-50"></div>
                <span className="text-muted-foreground">Peak (₱600)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded border-2 border-green-400 bg-green-50"></div>
                <span className="text-muted-foreground">Regular (₱500)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-slate-300"></div>
                <span className="text-muted-foreground">Unavailable</span>
              </div>
            </div>

            {/* Time Slot Selection */}
            <div className="space-y-2">
              <Label>
                Select Time Slots 
                <span className="text-muted-foreground ml-1">
                  ({rebookTimeSlots.length}/{selectedBooking?.duration_hours || 1} selected)
                </span>
                {loadingAvailability && <span className="text-xs text-primary ml-2">Loading...</span>}
              </Label>
              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                {loadingAvailability ? (
                  <div className="col-span-4 text-center py-4 text-muted-foreground text-sm">
                    Loading available time slots...
                  </div>
                ) : generateTimeSlots().map((slot) => {
                  const available = isSlotAvailable(slot);
                  const isSelected = rebookTimeSlots.includes(slot);
                  const hour = parseInt(slot.split(':')[0]);
                  const period = hour >= 12 ? 'PM' : 'AM';
                  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                  const priceInfo = getPriceInfoForSlot(hour, rebookDate);
                  
                  return (
                    <Button
                      key={slot}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      disabled={!available}
                      onClick={() => handleRebookSlotClick(slot)}
                      className={`text-xs relative ${!available ? 'opacity-50 cursor-not-allowed bg-slate-300 line-through' : ''} ${
                        !isSelected && available ? (priceInfo.isPeak 
                          ? 'border-orange-400 bg-orange-50 hover:bg-orange-100' 
                          : 'border-green-400 bg-green-50 hover:bg-green-100'
                        ) : ''
                      }`}
                    >
                      {hour12}{period}
                    </Button>
                  );
                })}
              </div>
              {rebookTimeSlots.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Selected: {getRebookTimeSlotsDisplay()}
                </p>
              )}
              {rebookTimeSlots.length > 0 && rebookTimeSlots.length !== (selectedBooking?.duration_hours || 1) && (
                <p className="text-sm text-amber-600">
                  Please select {(selectedBooking?.duration_hours || 1) - rebookTimeSlots.length} more slot{((selectedBooking?.duration_hours || 1) - rebookTimeSlots.length) > 1 ? 's' : ''}
                </p>
              )}

              {/* Peak/Regular Breakdown Comparison */}
              {rebookTimeSlots.length > 0 && (
                <div className="mt-2 p-2 bg-muted/50 rounded-md">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Selected breakdown:</p>
                  <div className="flex gap-2 text-xs">
                    {(() => {
                      const selected = getSelectedSlotsBreakdown();
                      const original = getOriginalBookingBreakdown();
                      const peakMatch = selected.peakHours === original.peakHours;
                      const regularMatch = selected.regularHours === original.regularHours;
                      
                      return (
                        <>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded ${peakMatch ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            Peak: {selected.peakHours}/{original.peakHours} {peakMatch ? '✓' : '✗'}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded ${regularMatch ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            Regular: {selected.regularHours}/{original.regularHours} {regularMatch ? '✓' : '✗'}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            {rebookCourt && rebookTimeSlots.length === (selectedBooking?.duration_hours || 1) && (
              <div className={`rounded-lg p-3 text-sm ${
                doesBreakdownMatch() 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                <p className={`font-medium mb-1 ${doesBreakdownMatch() ? 'text-green-800' : 'text-amber-800'}`}>
                  {doesBreakdownMatch() ? 'New Booking (Price Unchanged):' : 'Warning: Price Mismatch'}
                </p>
                <p className={doesBreakdownMatch() ? 'text-green-700' : 'text-amber-700'}>
                  {format(rebookDate, 'MMMM d, yyyy')}
                </p>
                <p className={doesBreakdownMatch() ? 'text-green-700' : 'text-amber-700'}>
                  {venue?.courts?.find(c => c.court_id === rebookCourt)?.name}
                </p>
                <p className={doesBreakdownMatch() ? 'text-green-700' : 'text-amber-700'}>
                  {getRebookTimeSlotsDisplay()} ({selectedBooking?.duration_hours || 1} hour{(selectedBooking?.duration_hours || 1) > 1 ? 's' : ''})
                </p>
                {!doesBreakdownMatch() && (
                  <p className="text-amber-600 text-xs mt-2">
                    <AlertTriangle className="h-3 w-3 inline mr-1" />
                    The peak/regular hour combination doesn&apos;t match. Please select different slots to keep the same price.
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 flex-shrink-0 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRebookModal(false);
                setRebookTimeSlots([]);
                setAvailability([]);
              }}
              disabled={rebooking}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRebook}
              disabled={rebooking || loadingAvailability || !rebookCourt || rebookTimeSlots.length !== (selectedBooking?.duration_hours || 1) || !doesBreakdownMatch()}
            >
              {rebooking ? 'Rescheduling...' : 'Confirm Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kiosk Transaction Details Modal */}
      <Dialog open={showKioskModal} onOpenChange={(open) => {
        if (!open) closeKioskModal();
        else setShowKioskModal(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-purple-600" />
              Kiosk Booking Details
            </DialogTitle>
          </DialogHeader>
          
          {kioskModalLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading transaction details...</span>
            </div>
          ) : selectedKioskTransaction ? (
            <div className="space-y-6">
              {/* Transaction Code */}
              <div className="flex items-center justify-between bg-purple-50 p-3 rounded-lg">
                <span className="text-sm text-muted-foreground">Transaction Code</span>
                <span className="font-mono font-semibold text-purple-700">
                  {selectedKioskTransaction.transaction_code}
                </span>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge className={
                  selectedKioskTransaction.status === 'paid' 
                    ? 'bg-green-100 text-green-800' 
                    : selectedKioskTransaction.status === 'cancelled'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-amber-100 text-amber-800'
                }>
                  {selectedKioskTransaction.status === 'paid' ? 'Paid' : 
                   selectedKioskTransaction.status === 'cancelled' ? 'Cancelled' : 'Pending Payment'}
                </Badge>
              </div>

              <Separator />

              {/* Booking Information */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Booking Information
                </h4>
                {(selectedKioskTransaction.booking_infos || []).map((info, idx) => (
                  <div key={idx} className="bg-muted/50 rounded-lg p-3 mb-2">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Date</span>
                        <p className="font-medium">{info.booking_date || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Court</span>
                        <p className="font-medium">Court {info.court_no || info.court || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Time</span>
                        <p className="font-medium">
                          {info.time_slots?.join(', ') || 
                           (info.booking_time_start ? `${formatTime12Hour(info.booking_time_start)} - ${formatTime12Hour(info.booking_time_end)}` : '-')}
                        </p>
                      </div>
                      {info.customer_name && (
                        <div>
                          <span className="text-muted-foreground">Customer</span>
                          <p className="font-medium">{info.customer_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Separator />

              {/* Payment Information */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Payment Information
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment Status</span>
                    <Badge className={
                      selectedKioskTransaction.status === 'paid' 
                        ? 'bg-emerald-100 text-emerald-800' 
                        : 'bg-amber-100 text-amber-800'
                    }>
                      {selectedKioskTransaction.status === 'paid' ? 'Paid' : 'Unpaid'}
                    </Badge>
                  </div>
                  {selectedKioskTransaction.payment_method && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payment Method</span>
                      <span className="font-medium">{formatPaymentMethod(selectedKioskTransaction.payment_method)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">Total Amount</span>
                    <span className="font-bold text-primary text-lg">{formatCurrency(selectedKioskTransaction.total_amount)}</span>
                  </div>
                </div>
              </div>

              {/* Created At */}
              <div className="text-xs text-muted-foreground text-center">
                Created on {new Date(selectedKioskTransaction.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
              No transaction details available
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeKioskModal}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {receiptBooking && (
            <BookingReceipt 
              payment={receiptBooking}
              showHeader={false}
              showActions={true}
              variant="confirmed"
            />
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MyBookingsPage;
